<template>
  <UForm
    ref="formRef"
    :state="form"
    :schema="schema"
    class="space-y-3"
    @submit="submit"
  >
    <UFormField name="rating">
      <UInputRating
        v-model="form.rating"
        empty-icon="i-local-empty-star"
        icon="i-ph-star-fill"
        color="warning"
        size="lg"
      />
    </UFormField>

    <UFormField name="author_name">
      <UInput
        v-model="form.author_name"
        class="w-full"
        placeholder="Ваше имя *"
        required
      />
    </UFormField>

    <UFormField name="email">
      <UInput
        v-model="form.email"
        class="w-full"
        placeholder="Ваша почта *"
        required
      />
    </UFormField>

    <UFormField name="content">
      <UTextarea
        v-model="form.content"
        class="w-full"
        placeholder="Оставьте сообщение"
        autoresize
        :ui="{
          base: 'min-h-25 pt-2.5',
        }"
      />
    </UFormField>

    <UFormField name="agree">
      <UCheckbox
        v-model="form.agree"
        class="mb-3"
        required
        :ui="{
          wrapper: 'font-lato! text-xs',
          label: 'typo-paragraph-xs-regular text-content-default',
        }"
      >
        <template #label>
          Я ознакомлен(-а) с <a
            href="/documents/personal-data"
            target="_blank"
            rel="noopener noreferrer"
            class="text-link"
          >
            Политикой обработки персональных данных
          </a>
          и даю своё
          <a
            href="/documents/personal-data"
            target="_blank"
            rel="noopener noreferrer"
            class="text-link"
          >
            согласие на обработку персональных данных</a
          >.
        </template>
      </UCheckbox>
    </UFormField>

    <UButton class="w-full mt-3" size="xl" type="submit">Отправить</UButton>
  </UForm>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui'
import { useDebounceFn } from '@vueuse/core'
import * as v from 'valibot'

defineOptions({
  name: 'ReviewForm',
})

const props = defineProps<{
  apiMethod: any
}>()

const { routeId } = useRouteId()
const toast = useToast()

const formRef = useTemplateRef('formRef')

const schema = v.object({
  rating: v.pipe(v.number(), v.minValue(1, 'Пожалуйста, поставьте оценку')),
  author_name: v.pipe(v.string(), v.trim(), v.minLength(1, 'Укажите ваше имя')),
  email: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(1, 'Укажите email'),
    v.email('Некорректный email'),
  ),
  content: v.pipe(v.string(), v.trim(), v.minLength(10, 'Минимум 10 символов')),
  agree: v.literal(true, 'Необходимо согласие с политикой'),
})

type FormType = v.InferOutput<typeof schema>

const getInitialForm = (): FormType => ({
  rating: 0,
  author_name: '',
  email: '',
  content: '',
  agree: false as unknown as true, // хак для валидации literal(true) при старте
})

const form = reactive(getInitialForm())

const resetForm = () => {
  Object.assign(form, getInitialForm())
  formRef.value?.clear()
}

const submit = useDebounceFn(async (event: FormSubmitEvent<FormType>) => {
  try {
    const { agree, ...backendData } = event.data
    await props.apiMethod(routeId, backendData)
    resetForm()

    toast.add({
      title: 'Отзыв отправлен',
      color: 'success',
      icon: 'i-lucide-check',
    })
  } catch ({ error }: any) {
    toast.add({
      title: 'Произошла ошибка при отправке отзыва',
      description: error?.message,
      color: 'error',
    })
  }
}, 1000)
</script>
